import { useEffect, useRef, useState } from 'react';
import { getSettings, patchSettings } from './api';
import { Activate } from './auth/Activate';
import { AuthProvider, useAuth } from './auth/AuthContext';
import { Login } from './auth/Login';
import { CertificateUpload } from './public/CertificateUpload';
import { AdminShell } from './shells/AdminShell';
import { CockpitShell } from './shells/CockpitShell';
import { PortalShell } from './shells/PortalShell';
import {
  applyDensity,
  applyTheme,
  getInitialDensity,
  getInitialTheme,
  persistDensity,
  persistTheme,
  type Density,
  type Theme,
} from './theme';

/** Read a magic-link activation token from the URL query, if present. */
function initialInviteToken(): string | null {
  if (typeof window === 'undefined') return null;
  return new URLSearchParams(window.location.search).get('token');
}

/**
 * Read a Story 6.9 passwordless upload token from the path `/certificate-upload/:token`,
 * if present. This is a PUBLIC, standalone page reached when logged out — resolved
 * before the auth gate and outside every app shell.
 */
function certificateUploadToken(): string | null {
  if (typeof window === 'undefined') return null;
  const m = /^\/certificate-upload\/([^/?#]+)/.exec(window.location.pathname);
  if (!m) return null;
  // Malformed percent-encoding (e.g. a bare `%`) would make decodeURIComponent throw
  // during render and blank the whole SPA on a PUBLIC entry point — fall back to the
  // raw segment so the page still loads (an invalid token then resolves to the uniform
  // "link can't be used" state via the API, never a crash).
  try {
    return decodeURIComponent(m[1]!);
  } catch {
    return m[1]!;
  }
}

/** Gate between activation, Login, and the role-scoped shell. */
function AppRoot(): React.JSX.Element {
  const { status, user } = useAuth();
  const [theme, setTheme] = useState<Theme>(() => getInitialTheme());
  const [density, setDensity] = useState<Density>(() => getInitialDensity());
  const [inviteToken, setInviteToken] = useState<string | null>(() => initialInviteToken());
  // Once the user changes theme/density, don't let an in-flight getSettings
  // resolve and clobber their choice with the stale server value.
  const settingsTouched = useRef(false);

  useEffect(() => {
    applyTheme(theme);
    persistTheme(theme);
  }, [theme]);

  useEffect(() => {
    applyDensity(density);
    persistDensity(density);
  }, [density]);

  // On sign-in, load the per-user preferences (server is the source of truth)
  // and apply them; the local cache above only prevents a first-paint flash.
  useEffect(() => {
    if (status !== 'authenticated') return;
    let cancelled = false;
    getSettings()
      .then((s) => {
        if (cancelled || settingsTouched.current) return;
        if (s.theme === 'light' || s.theme === 'dark') setTheme(s.theme);
        if (s.density === 'comfortable' || s.density === 'compact') setDensity(s.density);
      })
      .catch(() => {
        /* keep local/OS defaults if the fetch fails */
      });
    return () => {
      cancelled = true;
    };
  }, [status]);

  const toggleTheme = () => {
    settingsTouched.current = true;
    setTheme((t) => {
      const next = t === 'light' ? 'dark' : 'light';
      if (status === 'authenticated') void patchSettings({ theme: next }).catch(() => undefined);
      return next;
    });
  };

  const toggleDensity = () => {
    settingsTouched.current = true;
    setDensity((d) => {
      const next = d === 'comfortable' ? 'compact' : 'comfortable';
      if (status === 'authenticated') void patchSettings({ density: next }).catch(() => undefined);
      return next;
    });
  };

  // A signed-in user landing on a stale/reused ?token= (bookmark, back button)
  // keeps their session instead of being trapped on the activation screen —
  // just strip the leftover token from the URL.
  useEffect(() => {
    if (status === 'authenticated' && inviteToken) {
      if (typeof window !== 'undefined') {
        window.history.replaceState(null, '', window.location.pathname);
      }
      setInviteToken(null);
    }
  }, [status, inviteToken]);

  // The PUBLIC passwordless upload page (Story 6.9) is a standalone route reached via
  // the emailed link — it takes precedence over the auth gate entirely (no session
  // needed, no app shell) so it renders whether or not anyone is signed in.
  const uploadToken = certificateUploadToken();
  if (uploadToken) {
    return <CertificateUpload token={uploadToken} />;
  }

  // Magic-link activation takes precedence over the auth gate (Story 1.5), but
  // only for a user who is not already authenticated.
  if (inviteToken && status !== 'authenticated') {
    return (
      <Activate
        token={inviteToken}
        onDone={() => {
          // Strip ?token= from the URL and hand off to the role shell.
          if (typeof window !== 'undefined') {
            window.history.replaceState(null, '', window.location.pathname);
          }
          setInviteToken(null);
        }}
      />
    );
  }

  if (status === 'loading') {
    return (
      <div
        role="status"
        aria-live="polite"
        style={{
          minHeight: '100vh',
          display: 'grid',
          placeItems: 'center',
          background: 'var(--app-bg)',
        }}
      >
        <span className="muted">Loading…</span>
      </div>
    );
  }

  if (status === 'anonymous' || !user) {
    // `toggleTheme` already skips the server patch unless authenticated, so signed-out it
    // just applies + persists locally — exactly what the login surface needs.
    return <Login theme={theme} onToggleTheme={toggleTheme} />;
  }

  // The shell is chosen from the server-derived role — never a client toggle (UX-DR9).
  const shellProps = {
    theme,
    density,
    onToggleTheme: toggleTheme,
    onToggleDensity: toggleDensity,
  };
  switch (user.role) {
    case 'system_administrator':
      return <AdminShell {...shellProps} />;
    case 'vendor':
      return <PortalShell {...shellProps} />;
    case 'compliance_manager':
      return <CockpitShell {...shellProps} />;
    default:
      // Fail closed: an unrecognized role gets no shell, not the Manager cockpit.
      return (
        <div
          role="alert"
          style={{
            minHeight: '100vh',
            display: 'grid',
            placeItems: 'center',
            background: 'var(--app-bg)',
          }}
        >
          <span className="muted">Your account role is not supported.</span>
        </div>
      );
  }
}

export { AppRoot };

export function App(): React.JSX.Element {
  return (
    <AuthProvider>
      <AppRoot />
    </AuthProvider>
  );
}
